// Feature-specific tutorial trigger - shows for page-specific help
// Always shows if tutorial is completed or user exited early
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTutorial } from './useTutorial';
import tutorialConfig from './utils/tutorialConfig.json';
import { buildSmartChartSection, waitForSmartChartAnchors } from './utils/smartChartSlides';
import sendServerLogging from '../../utils/sendServerLogging';
import userJSON from '../../utils/userJSON';
import { isStandaloneUser as getIsStandaloneUser } from "../../utils/standaloneUser";

const FeatureTutorialTrigger = () => {
  const location = useLocation();
  const isStandaloneUser = getIsStandaloneUser();
  const {
    startTutorial,
    isActive,
    setTutorialSteps,
    exitTutorial,
    tutorialData,
  } = useTutorial();

  // The record-page help starts on the green "Start Recording" button, so the
  // launcher is only useful while that button is on screen. It belongs to
  // AudioRecorder's own state, which doesn't re-render this component, so watch
  // the DOM for it.
  const isRecordPage = location.pathname.startsWith('/record');
  const [hasStartRecordingButton, setHasStartRecordingButton] = useState(false);
  useEffect(() => {
    if (!isRecordPage || isStandaloneUser) return undefined;
    const check = () =>
      setHasStartRecordingButton(Boolean(document.querySelector('#start_recording')));
    check();
    const intervalId = setInterval(check, 300);
    return () => clearInterval(intervalId);
  }, [isRecordPage, isStandaloneUser]);

  if (isStandaloneUser) {
    return null;
  }

  if (isRecordPage && !hasStartRecordingButton) {
    return null;
  }
  
  // Don't show if tutorial is active or loading
  if (isActive || !tutorialData || !tutorialData.onboarding) {

    return null;
  }

  // Shown regardless of onboarding completion so pages that render only this
  // trigger (e.g. the record page, while Start Recording is up) have an "i"
  // launcher available. Pages that also render FullTutorialTrigger must not
  // render this one too, or two launchers would overlap.


  
  // Determine which section to load based on current route
  const getTutorialSection = () => {
    const pathname = location.pathname;
    
    // Check if edit patient modal is open (Bootstrap modal with modal-xl class)
    const editPatientModal = document.querySelector('.modal.show.modal-xl');
    if (editPatientModal) {
      const section = tutorialConfig.sections.find(s => s.id === 'review_upload');
      if (section) {
        // Review-modal help covers the modal's own slides only: start at
        // Review Note Page and END at Save and Upload Note (locked_note runs
        // on the dashboard after the modal closes, so it is excluded here).
        const startStepIndex = section.steps.findIndex(step => step.id === 'review_note_page');
        const endStepIndex = section.steps.findIndex(step => step.id === 'save_upload_note');
        if (startStepIndex !== -1 && endStepIndex >= startStepIndex) {
          return [{
            ...section,
            steps: section.steps.slice(startStepIndex, endStepIndex + 1)
          }];
        }
        if (startStepIndex !== -1) {
          return [{
            ...section,
            steps: section.steps.slice(startStepIndex)
          }];
        }
        return [section];
      }
      return [];
    }
    
    // Map routes to section IDs
    if (pathname === '/dashboard') {
      const section = tutorialConfig.sections.find(s => s.id === 'dashboard_intro');
      if (section) {
        // Start from dashboard_overview step when on dashboard page
        const startStepIndex = section.steps.findIndex(step => step.id === 'dashboard_overview');
        if (startStepIndex !== -1) {
          return [{
            ...section,
            steps: section.steps.slice(startStepIndex)
          }];
        }
        return [section];
      }
      return [];
    } else if (pathname.startsWith('/record')) {
      const section = tutorialConfig.sections.find(s => s.id === 'record');
      if (section) {
        // Feature tutorial on record page: start at telehealth_setup and end at submit_recording
        const startStepIndex = section.steps.findIndex(step => step.id === 'telehealth_setup');
        const endStepIndex = section.steps.findIndex(step => step.id === 'submit_recording');
        if (startStepIndex !== -1 && endStepIndex !== -1 && endStepIndex >= startStepIndex) {
          return [{
            ...section,
            steps: section.steps.slice(startStepIndex, endStepIndex + 1)
          }];
        }
        if (startStepIndex !== -1) {
          return [{
            ...section,
            steps: section.steps.slice(startStepIndex)
          }];
        }
        return [section];
      }
      return [];
    } else if (pathname === '/notes') {
      const section = tutorialConfig.sections.find(s => s.id === 'dashboard_intro');
      return section ? [section] : [];
    } else if (pathname === '/settings') {
      // Settings help covers the settings slides only — the Contact Support
      // section is intentionally excluded.
      const section = tutorialConfig.sections.find(s => s.id === 'settings');
      if (section) {
        // Start from navigate_settings step to show the settings page overview
        const startStepIndex = section.steps.findIndex(step => step.id === 'navigate_settings');
        if (startStepIndex !== -1) {
          return [{
            ...section,
            steps: section.steps.slice(startStepIndex),
          }];
        }
        return [section];
      }
      return [];
    }
    
    return [];
  };

  const handleStartTutorial = async () => {
    // If the Smart Chart window is open, scope help to the Smart Chart and
    // Smart Assist slides right where the user is, instead of the whole route
    // section. Mirrors the context-aware launcher so both "i" buttons behave
    // the same when the modal is open.
    const isSmartChartModalOpen = Boolean(
      document.querySelector('#precharting_modal_dialog')
    );
    if (isSmartChartModalOpen) {
      // The sequence starts on the Smart Chart tab; only one tabpanel is
      // mounted at a time, so switch back if the user is on Custom Prompt.
      const smartChartTab = document.querySelector('#smart-chart-tab');
      if (smartChartTab && smartChartTab.getAttribute('aria-selected') !== 'true') {
        smartChartTab.click();
      }
      // Build only once that panel and its anchors are mounted, so the tour
      // isn't missing the slides that anchor inside it.
      await waitForSmartChartAnchors();
      const smartChartSection = buildSmartChartSection({ includeSmartAssist: true });
      if (smartChartSection) {
        setTutorialSteps([]);
        await new Promise((resolve) => setTimeout(resolve, 0));
        setTutorialSteps([smartChartSection]);
        await startTutorial({
          resume: false,
          startSectionId: smartChartSection.id,
          startStepId: smartChartSection.steps[0].id,
          expectedSteps: smartChartSection.steps,
          isFullTutorial: false,
        });
        return;
      }
    }

    const sections = getTutorialSection();
    if (sections.length > 0) {
      // Send server log for feature tutorial trigger click
      const sectionId = sections[0]?.id || '';
      sendServerLogging({
        body: {
          ...userJSON(localStorage.getItem("userName")),
          event: 'CLICKED_FEATURE_TUTORIAL_TRIGGER',
          eventData: {
            sectionId: sectionId,
            pathname: location.pathname,
            patientId: '',
            mrn: ''
          }
        }
      });
      
      // Reset any active tutorial state first
      if (isActive) {
        exitTutorial();
        // Wait a bit for state to reset
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Clear old steps first to ensure clean state
      setTutorialSteps([]);
      // Wait a tick for the clear to propagate
      await new Promise(resolve => setTimeout(resolve, 0));
      
      // Set the tutorial steps - this will trigger useEffect to update flattenedSteps
      setTutorialSteps(sections);
      
      // startTutorial will now wait for flattenedSteps to be ready
      await startTutorial();
    } else {
    }
  };
  
  return (
    <button
      type="button"
      onClick={handleStartTutorial}
      className="tutorial-launcher fixed bottom-6 z-[99999] inline-flex h-12 w-12 items-center justify-center rounded-full bg-accent1 text-white shadow-md transition duration-200 hover:scale-110 hover:bg-purple2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent1 focus-visible:ring-offset-2"
      aria-label="Start tutorial"
    >
      <svg
        className="h-6 w-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" strokeWidth="2" />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M12 16v-4m0-4h.01"
        />
      </svg>
    </button>
  );
};

// Export as both names for backward compatibility
const TutorialTrigger = FeatureTutorialTrigger;
export default FeatureTutorialTrigger;
export { FeatureTutorialTrigger };